"use client";
import { useState, useEffect } from "react";
import { ArrowUpRight, GithubIcon } from "lucide-react";
import Link from "next/link";
import Image from "next/image";

const Projects = [
  {
    id: 1,
    category: "SaaS",
    srcImage:
      "https://res.cloudinary.com/dyv3dluov/image/upload/v1731941666/portfolio/projects/wzsadnshv94gnghqxmgk.png",
    alt: "SECURESIST cybersecurity platform",
    title: "SECURESIST",
    description:
      "Production cybersecurity SaaS used to train employees and simulate threats. Role-based dashboards, training, quizzes, certificates, analytics, and a multilingual Next.js marketing site.",
    techStack: ["React", "Vite", "Next.js", "TypeScript", "Tailwind", "React Query"],
    featured: true,
    projectUrl: "https://securesist.com",
    gitUrl: "https://github.com/RXROW",
  },
  {
    id: 2,
    category: "Fullstack",
    srcImage:
      "https://res.cloudinary.com/dyv3dluov/image/upload/v1731940286/portfolio/projects/ecommerce.png",
    alt: "Multi Vendor Ecommerce",
    title: "Multi Vendor Ecommerce",
    description:
      "A modern marketplace built with Next.js. Vendors manage stores and products; customers get search, filtering, and a smooth checkout experience.",
    techStack: ["Next.js", "Tailwind", "Prisma", "NextAuth", "Redux"],
    featured: true,
    projectUrl: "https://kachabazar-store-nine.vercel.app",
    gitUrl: "https://github.com/RXROW/Multi-Vendor-Ecommerce_NextJs",
  },
  {
    id: 3,
    category: "Fullstack",
    srcImage:
      "https://res.cloudinary.com/dyv3dluov/image/upload/v1731940276/portfolio/projects/ai%20gen.png",
    alt: "AI Content Generator",
    title: "AI Content Generator",
    description:
      "AI content generator built with Next.js and Gemini for high-quality, context-aware writing workflows.",
    techStack: ["Next.js", "Gemini AI", "Drizzle ORM", "Tailwind", "TypeScript"],
    featured: true,
    projectUrl: "https://ai-content-generator-next-js.vercel.app",
    gitUrl: "https://github.com/RXROW/AI-Content-Generator-NextJS-Gemini",
  },
  {
    id: 4,
    category: "Frontend",
    srcImage:
      "https://res.cloudinary.com/dyv3dluov/image/upload/v1731941666/portfolio/projects/wzsadnshv94gnghqxmgk.png",
    alt: "Astra Agency website",
    title: "Astra Agency website",
    description:
      "Agency marketing site built with Next.js and Aceternity UI — fast, modern, and built to showcase services.",
    techStack: ["Next.js", "Aceternity UI", "Tailwind"],
    featured: true,
    projectUrl: "https://astra-agency.netlify.app",
    gitUrl: "https://github.com/RXROW/Astra-website-",
  },
  {
    id: 5,
    category: "Frontend",
    srcImage:
      "https://res.cloudinary.com/dyv3dluov/image/upload/v1732015675/portfolio/projects/fjuiyjdzig0rqlosooj5.jpg",
    alt: "React Admin Dashboard",
    title: "React Admin Dashboard",
    description:
      "Admin dashboard with real-time-friendly UI, clear navigation, and customizable data views.",
    techStack: ["React", "Syncfusion", "Tailwind"],
    featured: true,
    projectUrl: "https://react-admin-dashbourd.netlify.app",
    gitUrl: "https://github.com/RXROW/react_admin_dashboard",
  },
  {
    id: 6,
    category: "Frontend",
    srcImage:
      "https://res.cloudinary.com/dyv3dluov/image/upload/v1731940286/portfolio/projects/ecommerce.png",
    alt: "Hotel Booking Management System",
    title: "Hotel Booking System",
    description:
      "Hotel booking platform with rooms, date-based booking, reviews, and admin tools. UI redesign improved booking efficiency by 40%.",
    techStack: ["React", "TypeScript", "MUI", "Context API", "Axios"],
    featured: true,
    projectUrl: "",
    gitUrl: "https://github.com/RXROW",
  },
];

const ProjectCard = ({ project }) => {
  return (
    <div className="group relative h-full rounded-2xl border border-white/10 bg-neutral-950 overflow-hidden transition-all duration-300 hover:border-violet-500/40">
      <div className="relative aspect-[16/10] overflow-hidden">
        <Image
          src={project.srcImage}
          alt={project.alt}
          width={800}
          height={1000}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
          {project.projectUrl ? (
            <Link
              target="_blank"
              href={project.projectUrl}
              className="bg-white text-black px-4 py-2 rounded-full flex items-center gap-2 text-sm"
            >
              Demo <ArrowUpRight className="w-4 h-4" />
            </Link>
          ) : null}
          <Link
            target="_blank"
            href={project.gitUrl}
            className="bg-neutral-800 text-white px-4 py-2 rounded-full flex items-center gap-2 text-sm"
          >
            Repo <GithubIcon className="h-4 w-4" />
          </Link>
        </div>
      </div>
      <div className="p-6">
        <div className="flex justify-between items-start mb-3 gap-3">
          <h3 className="text-xl font-semibold text-white">{project.title}</h3>
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/20">
            {project.category}
          </span>
        </div>
        <p className="text-neutral-400 text-sm mb-4">{project.description}</p>
        <div className="flex flex-wrap gap-2">
          {project.techStack.map((tech) => (
            <span
              key={tech}
              className="px-3 py-1 text-xs font-medium rounded-full bg-white/5 text-neutral-300 border border-white/10"
            >
              {tech}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [filteredProjects, setFilteredProjects] = useState(Projects);
  const [isLoading, setIsLoading] = useState(false);

  const categories = ["all", ...new Set(Projects.map((project) => project.category))];

  useEffect(() => {
    setIsLoading(true);
    const filtered =
      selectedCategory === "all"
        ? Projects
        : Projects.filter((project) => project.category === selectedCategory);

    setFilteredProjects(filtered);
    const timer = setTimeout(() => setIsLoading(false), 180);
    return () => clearTimeout(timer);
  }, [selectedCategory]);

  return (
    <div className="text-white">
      <div className="flex justify-center flex-wrap gap-2 pb-8">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 text-sm rounded-full border transition-all ${
              selectedCategory === category
                ? "border-violet-400 text-white bg-violet-500/20"
                : "border-white/10 text-neutral-400 hover:text-white"
            }`}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredProjects.map((project) => (
          <div
            key={project.id}
            className={`transition-all duration-500 ${
              isLoading ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
            }`}
          >
            <ProjectCard project={project} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Gallery;
